// 🔐 accounts (09-30, ACCOUNTS_SIGNIN_SPEC_2026_09_30.md §3.7-§3.8): the one
// place that knows whether this browser is signed in. A tiny module store
// (no React provider): pages read it with useAccount(), and plain code (the
// room's seat effect) calls the functions below.
//
// INERT while sign-in is off: signInOn() false → status 'off', no network call,
// seatForRoom() answers null at once. Signed-out visitors (no token) never wait
// on a lookup either.
//
// Pre-ship review (09-30, spec §10.5): sign-in ASKS before it attaches what this
// browser holds (R10, Nina: "Ask first"); a session that ended elsewhere (sign
// out everywhere, delete, lapsed) takes back what the account brought here; a
// seat made while signed in leaves with the account; the seat lookup has a
// deadline, so it can never hold a room's seat hostage.
import { signInOn } from './signInSwitch';
import * as api from './accountClient';
import { collectLocalSeats, collectLocalAgents, adoptPulledSeat, forgetPulledSeats, rememberAccountSeat } from './localCreds';

export const TOKEN_KEY = 'myspaice_session_v1';
// A cold api container + config + me + seat. Past this the room goes on without
// the account (the old path), and a late answer is never adopted.
const SEAT_LOOKUP_MS = 8000;

// 'loading' (not 'off') when sign-in is on, so /account never flashes "not open yet".
const firstStatus = () => { try { return signInOn() ? 'loading' : 'off'; } catch (e) { return 'off'; } };
let state = { status: firstStatus(), account: null, summary: null, googleClientId: null };
const listeners = new Set();
const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => { try { fn(state); } catch (e) { /* a listener must not break the others */ } });
};
export const getAccountState = () => state;
export function subscribeAccount(fn) { listeners.add(fn); return () => listeners.delete(fn); }

const storage = () => { try { return window.localStorage; } catch (e) { return null; } };
export function getToken() { try { return storage()?.getItem(TOKEN_KEY) || null; } catch (e) { return null; } }
function setToken(token) {
  try { if (token) storage().setItem(TOKEN_KEY, token); else storage().removeItem(TOKEN_KEY); } catch (e) { /* private mode */ }
}
const counts = () => {
  const s = state.summary || {};
  return { rooms: (s.rooms || []).length, agents: (s.agents || []).length };
};

let initPromise = null;
export function initAccount() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    if (!signInOn()) { set({ status: 'off' }); return state; }
    set({ status: 'loading' });
    const cfg = await api.authConfig();
    // Unreachable (offline, 5xx): off for now, and ask again next time, never for the whole page.
    if (!cfg.success) { initPromise = null; set({ status: 'off' }); return state; }
    if (!cfg.enabled || !cfg.google_client_id) { set({ status: 'off' }); return state; }
    set({ googleClientId: cfg.google_client_id });
    if (!getToken()) { set({ status: 'signed_out' }); return state; }
    await refreshAccount();
    return state;
  })().catch(() => { initPromise = null; set({ status: 'off' }); return state; });
  return initPromise;
}

export async function refreshAccount() {
  const token = getToken();
  if (!token) { set({ status: 'signed_out', account: null, summary: null }); return state; }
  const r = await api.authMe(token);
  if (getToken() !== token) return state; // signed out (or in again) meanwhile
  if (r.success) set({ status: 'signed_in', account: r.account, summary: r });
  else if (r.code === 'signed_out') signedOutLocally(); // ended elsewhere: what it brought here goes too
  else if (r.code === 'auth_off') set({ status: 'off' }); // the backend switch went off: hide every door, keep the token
  else if (state.status === 'loading') set({ status: 'signed_out' }); // offline: show the door, keep the token
  return state;
}

// ── R10 "Ask first" (Nina 09-30): sign-in attaches only what the person ticks ──
let lastFound = null; // { seats, agents } sent with the last preview, so the ticks map back to secrets

/**
 * What this browser holds that the account could keep, without keeping it:
 * { rooms: [{ room_id, room_name, seat_name, made, seats }], agents: [{ i, agent_id, name, avatar_url }], taken }
 * Only 'new' things are listed; `taken` counts the ones another account holds.
 */
export async function findOnThisBrowser() {
  const token = getToken();
  const store = storage();
  if (!token || !store) return null;
  const seats = collectLocalSeats(store);
  const agents = collectLocalAgents(store);
  if (!seats.length && !agents.length) return { rooms: [], agents: [], taken: 0 };
  const r = await api.authClaimPreview(token, { seats, agents });
  if (!r.success) return null;
  lastFound = { seats, agents };
  const all = [...(r.rooms || []), ...(r.agents || [])];
  return {
    rooms: (r.rooms || []).filter((x) => x.status === 'new'),
    agents: (r.agents || []).filter((x) => x.status === 'new'),
    taken: all.filter((x) => x.status === 'taken').length,
  };
}

/** "Keep these": attach exactly the ticked rooms (every proven seat in each) and AIs. */
export async function keepFound({ rooms = [], agents = [] } = {}) {
  const token = getToken();
  const found = lastFound;
  lastFound = null;
  if (!token || !found) return { ok: false, ...counts() };
  const wanted = new Set(rooms.flatMap((r) => r.seats || []));
  const seats = found.seats.filter((s) => wanted.has(s.player_id));
  const keys = agents.map((a) => found.agents[a.i]).filter(Boolean);
  let ok = true;
  if (seats.length || keys.length) ok = !!(await api.authClaim(token, { seats, agents: keys })).success;
  await refreshAccount();
  return { ok, ...counts() };
}

/**
 * The Google button's callback lands here. Nothing on this browser is attached
 * yet: `found` (or null) is what the sheet lists next.
 */
export async function signInWithGoogle(credential) {
  const r = await api.authGoogle(credential);
  if (!r.success || !r.token) return { ok: false, error: r.error || "That sign-in didn't work. Try again." };
  setToken(r.token);
  let found = null;
  try { found = await findOnThisBrowser(); } catch (e) { found = null; }
  await refreshAccount();
  const listed = found && (found.rooms.length || found.agents.length) ? found : null;
  return { ok: true, found: listed, taken: found ? found.taken : 0, ...counts() };
}

/**
 * After this device mints a seat while signed in: attach it (fire-and-forget).
 * Once the account holds it, it leaves this device at sign-out like a pulled
 * seat (a friend's laptop keeps nothing of yours) and comes back with the account.
 */
export function claimSeat(playerId, roomId) {
  const token = getToken();
  const store = storage();
  if (state.status !== 'signed_in' || !token || !store || !playerId) return;
  const secret = store.getItem(`player_secret_${playerId}`);
  if (!secret) return;
  api.authClaim(token, { seats: [{ player_id: playerId, player_secret: secret }] }).then((r) => {
    const res = r && r.success && r.seats && r.seats[0] && r.seats[0].result;
    if ((res === 'linked' || res === 'yours') && roomId && getToken() === token) {
      rememberAccountSeat(storage(), { seat: playerId, room: roomId });
    }
  }).catch(() => {});
}

/**
 * Your seat follows you: before a room mints a stranger, ask the account for
 * your seat there. Answers { player_id, player_secret, name, pulled } or null.
 * The seat is stored on this device (and remembered as pulled, for sign-out).
 * Never throws, never waits past SEAT_LOOKUP_MS, and adopts nothing that
 * arrives after the deadline or after a sign-out.
 */
export async function seatForRoom(roomId, { timeoutMs = SEAT_LOOKUP_MS } = {}) {
  if (!roomId || !signInOn() || !getToken()) return null;
  let late = false;
  let timer = null;
  const lookup = (async () => {
    await initAccount();
    if (state.status !== 'signed_in') return null;
    const token = getToken();
    if (!token) return null;
    const r = await api.authSeat(token, roomId);
    if (!r.success) {
      if (r.code === 'signed_out' && getToken() === token) signedOutLocally();
      return null;
    }
    if (late || getToken() !== token) return null;
    const seat = r.seat;
    if (!seat || !seat.player_id || !seat.player_secret) return null;
    const store = storage();
    const pulled = !!store && store.getItem(`player_secret_${seat.player_id}`) !== seat.player_secret;
    adoptPulledSeat(store, { seat: seat.player_id, room: roomId, secret: seat.player_secret });
    return { ...seat, pulled };
  })().catch(() => null);
  const deadline = new Promise((resolve) => { timer = setTimeout(() => { late = true; resolve(null); }, timeoutMs); });
  try { return await Promise.race([lookup, deadline]); } finally { clearTimeout(timer); }
}

function signedOutLocally() {
  setToken(null);
  lastFound = null;
  forgetPulledSeats(storage());
  set({ status: 'signed_out', account: null, summary: null });
}

/**
 * This device: best effort, it forgets either way. Everywhere: it must really
 * happen, so a failure keeps this sign-in and says so (the person can retry).
 */
export async function signOut({ everywhere = false } = {}) {
  const token = getToken();
  if (everywhere) {
    const r = token ? await api.authSignOutAll(token) : { success: false, code: 'signed_out' };
    if (!r.success) {
      if (r.code === 'signed_out') {
        signedOutLocally();
        return { ok: false, error: 'That sign-in had already ended. Sign in again to sign out everywhere.' };
      }
      return { ok: false, error: r.error || 'That did not work. Try again.' };
    }
  } else if (token) {
    await api.authSignOut(token);
  }
  signedOutLocally();
  return { ok: true };
}

export async function deleteAccount() {
  const token = getToken();
  if (!token) return { ok: false, error: 'Please sign in again.' };
  const r = await api.authDelete(token);
  if (!r.success) return { ok: false, error: r.error || 'That did not work. Try again.' };
  signedOutLocally();
  return { ok: true };
}

/** "Remove" on /account: the account lets go of a room or an AI. This device keeps what it holds. */
export async function removeFromAccount({ kind, id }) {
  const token = getToken();
  if (!token) return { ok: false, error: 'Please sign in again.' };
  const r = await api.authUnlink(token, { kind, id });
  if (!r.success) {
    if (r.code === 'signed_out') signedOutLocally();
    return { ok: false, error: r.error || 'That did not work. Try again.' };
  }
  await refreshAccount();
  return { ok: true };
}

/** "n•••@gmail.com": enough to recognize your own account, never the whole address on screen. */
export function maskEmail(email) {
  const s = String(email || '');
  const at = s.indexOf('@');
  if (at < 1) return '';
  return `${s[0]}•••${s.slice(at)}`;
}

export const initialOf = (email) => (String(email || '').trim()[0] || '?').toUpperCase();

// Tests only: reset the module between cases.
export function _resetAccountForTests() { state = { status: 'off', account: null, summary: null, googleClientId: null }; initPromise = null; lastFound = null; listeners.clear(); }
